'use client'
import clsx from 'clsx'
import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Home, Calendar, Clock, Folder, User, FileText } from 'react-feather'
import { Trans } from '@lingui/react/macro'

import { Link } from '@/lib/i18n/settings'
import { useTranslations, usePathname } from '@/lib/next-shim'

import { useLanguageSync } from '../model/useLanguageSync'
import { Header } from './Header'
import { Searchbar } from './Searchbar'

type AccountLayoutProps = {
  children?: React.ReactNode
}

export function AccountLayout({ children }: AccountLayoutProps) {
  const t = useTranslations()
  const pathname = usePathname()
  // The live teleconsultation needs the full viewport height; hide the marketing footer there.
  const isLiveConsultation = /\/account\/consultations\/[^/]+\/live$/.test(pathname)
  const [openSearchBar, setOpenSearchBar] = useState(false)

  // Sync language preference with backend
  // This ensures emails and notifications are sent in the patient's preferred language
  useLanguageSync()

  const navigationMenu = [
    {
      icon: <Home size={24} />,
      label: t('account.navigationMenu.accountDashboard.label'),
      name: 'dashboard',
      path: '/account/dashboard',
    },
    {
      icon: <Calendar size={24} />,
      label: t('account.navigationMenu.accountConsultations.label'),
      name: 'consultations',
      path: '/account/consultations',
    },
    {
      icon: <Clock size={24} />,
      label: t('account.sections.Orders.title'),
      name: 'orders',
      path: '/account/orders',
    },
    {
      icon: <FileText size={24} />,
      label: t('account.navigationMenu.accountExpertises.label'),
      name: 'expertises',
      path: '/account/expertises',
    },
    {
      icon: <Folder size={24} />,
      label: t('account.navigationMenu.accountDocuments.label'),
      name: 'documents',
      path: '/account/documents',
    },
    {
      icon: <User size={24} />,
      label: t('account.navigationMenu.accountProfile.label'),
      name: 'profile',
      path: '/account/profile',
    },
  ]

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-[9999] focus:top-2 focus:left-2 focus:bg-white focus:text-primary focus:px-4 focus:py-2 focus:rounded focus:shadow-lg focus:outline focus:outline-2 focus:outline-primary focus:font-semibold"
        >
          {t('a11y.skipToMainContent')}
        </a>
        <Header searchBar={() => setOpenSearchBar(!openSearchBar)} menu={navigationMenu} />
        <main id="main-content" className="flex flex-1 flex-row min-w-0 w-full">
          <div className="w-full flex flex-1 md:flex-row flex-col min-w-0 bg-gray-50 pb-14 md:pb-0">
            {children || <Outlet />}
            <Bottombar menu={navigationMenu} />
          </div>
        </main>
        {!isLiveConsultation && (
          <footer role="contentinfo" className="bg-white border-t border-gray-200 py-4 px-6 mb-14 md:mb-0 text-center text-sm text-gray-500">
            <p>
              &copy; {new Date().getFullYear()} Medconnect &mdash;{' '}
              <Link href="/accessibility" className="underline hover:text-primary">
                <Trans>Accessibility</Trans>
              </Link>
              {' | '}
              <Link href="/help" className="underline hover:text-primary">
                <Trans>Help & Support</Trans>
              </Link>
            </p>
          </footer>
        )}
      </div>
      <div aria-live="polite" aria-atomic="true" className="sr-only" id="status-announcer" />
      <Searchbar open={openSearchBar} onOpen={() => setOpenSearchBar(!openSearchBar)} />
    </>
  )
}

type BottombarProps = {
  menu: Array<{
    icon: React.ReactNode
    label: string
    name: string
    path: string
  }>
}

function Bottombar(props: BottombarProps) {
  const { menu } = props
  const pathname = usePathname()

  return (
    <div className="md:hidden block relative w-full z-10">
      <nav aria-label="Mobile navigation" className="h-14 fixed bottom-0 inset-x-0 bg-gray-50 flex justify-between text-sm">
        <ul className="flex w-full list-none m-0 p-0">
          {menu.map((menuItem) => {
            const isCurrent = pathname?.startsWith(menuItem.path)
            return (
              <li key={menuItem.name} className="flex-1">
                <Link
                  href={menuItem.path}
                  aria-label={menuItem.label}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={clsx(
                    isCurrent ? 'bg-gray-50 text-blue-500' : 'text-gray-700 hover:text-blue-700  hover:bg-gray-50',
                    `w-full block px-3 text-center transition duration-300`
                  )}
                >
                  <div className="cursor-pointer flex flex-col justify-center items-center w-full h-full">
                    <div aria-hidden="true" className={clsx(isCurrent ? 'text-blue-500' : 'text-gray-700 group-hover:text-blue-700', 'flex shrink-0')}>
                      {menuItem.icon}
                    </div>
                  </div>
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </div>
  )
}
